import type { ModalData } from './typings'

import { useIntl } from '@/intl'
import { useState } from 'react'

type ErrorType = Exclude<ModalData['issue'], 'none'>

export function useTexts() {
  const { translate } = useIntl()

  const [api] = useState(() => ({
    headerTitleSingle: translate({ id: 'modals.moveOrders.header.title.single' }),

    headerTitlePaired: (
      orderStepStatus: 'not-executed' | 'not-completed' | 'executed',
      pairedOrderStepStatus:
        | 'not-executed'
        | 'not-completed'
        | 'executed'
        | 'not-assigned'
        | 'in-a-locked-route',
      pairedOrderStepType: uui.domain.client.rm.OrderStepIdentifier,
    ) => {
      switch (orderStepStatus) {
        case 'not-executed': {
          switch (pairedOrderStepStatus) {
            case 'not-executed':
            case 'not-completed': {
              // Move paired order as well?
              return translate({ id: 'modals.moveOrders.header.title' })
            }
            case 'executed': {
              // Move executed pickup/dropoff as well?
              switch (pairedOrderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.header.title.moveExecutedPairedPickup',
                  })
                case 'd':
                  return translate({
                    id: 'modals.moveOrders.header.title.moveExecutedPairedDropoff',
                  })
              }
            }
            case 'not-assigned': {
              // Assign paired pickup/dropoff as well?
              switch (pairedOrderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.header.title.assignPairedPickup',
                  })
                case 'd':
                  return translate({
                    id: 'modals.moveOrders.header.title.assignPairedDropoff',
                  })
              }
            }
            case 'in-a-locked-route': {
              // Paired order can't be moved
              return translate({
                id: 'modals.moveOrders.header.title.pairedCantBeMoved',
              })
            }
          }
        }
        case 'not-completed': {
          switch (pairedOrderStepStatus) {
            case 'not-executed':
            case 'not-completed': {
              // Move paired order as well?
              return translate({ id: 'modals.moveOrders.header.title' })
            }
            case 'executed': {
              // Move executed pickup/dropoff as well?
              switch (pairedOrderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.header.title.moveExecutedPairedPickup',
                  })
                case 'd':
                  return translate({
                    id: 'modals.moveOrders.header.title.moveExecutedPairedDropoff',
                  })
              }
            }
            case 'not-assigned': {
              // Assign paired pickup/dropoff as well?
              switch (pairedOrderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.header.title.assignPairedPickup',
                  })
                case 'd':
                  return translate({
                    id: 'modals.moveOrders.header.title.assignPairedDropoff',
                  })
              }
            }
            case 'in-a-locked-route': {
              // Paired order can't be moved
              return translate({
                id: 'modals.moveOrders.header.title.pairedCantBeMoved',
              })
            }
          }
        }
        case 'executed': {
          switch (pairedOrderStepStatus) {
            case 'not-executed':
            case 'not-completed': {
              // Move executed order and paired pickup/dropoff?
              switch (pairedOrderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.header.title.moveExecutedOrderAndPairedPickup',
                  })
                case 'd':
                  return translate({
                    id: 'modals.moveOrders.header.title.moveExecutedOrderAndPairedDropoff',
                  })
              }
            }
            case 'executed': {
              // Move executed orders?
              return translate({
                id: 'modals.moveOrders.header.title.moveExecutedOrders',
              })
            }
            case 'not-assigned': {
              // Assign paired pickup/dropoff as well?
              switch (pairedOrderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.header.title.assignPairedPickup',
                  })
                case 'd':
                  return translate({
                    id: 'modals.moveOrders.header.title.assignPairedDropoff',
                  })
              }
            }
            case 'in-a-locked-route': {
              // Move executed order?
              return translate({
                id: 'modals.moveOrders.header.title.moveExecutedOrder',
              })
            }
          }
        }
      }
    },

    btnDoNotMove: (stepIdentifier: uui.domain.client.rm.OrderStepIdentifier) => {
      const pickUp = stepIdentifier === 'p'

      return pickUp
        ? translate({ id: 'modals.moveOrders.button.doNotMove.unassigned.delivery' })
        : translate({ id: 'modals.moveOrders.button.doNotMove.unassigned.pickup' })
    },

    btnCancel: translate({ id: 'modals.moveOrders.button.cancel' }),

    btnMoveSingle: translate({ id: 'modals.moveOrders.button.move.single' }),

    btnMovePaired: (
      stepIdentifier: uui.domain.client.rm.OrderStepIdentifier,
      pairedUnassigned: boolean,
    ) => {
      const pickUp = stepIdentifier === 'p'
      if (pickUp) {
        return pairedUnassigned
          ? translate({ id: 'modals.moveOrders.button.move.pickup.unassigned' })
          : translate({ id: 'modals.moveOrders.button.move.moveBoth' })
      }

      return pairedUnassigned
        ? translate({ id: 'modals.moveOrders.button.move.delivery.unassigned' })
        : translate({ id: 'modals.moveOrders.button.move.moveBoth' })
    },

    btnCancelTitle: translate({ id: 'modals.moveOrders.button.cancel.title' }),

    savePreference: translate({ id: 'modals.moveOrders.savePreferenceText' }),

    cardTitleSingle: translate({ id: 'modals.moveOrders.card.title.single' }),

    cardTitlePaired: (
      orderStepStatus: 'not-executed' | 'not-completed' | 'executed',
      orderStepType: uui.domain.client.rm.OrderStepIdentifier,
      pairedOrderStepStatus:
        | 'not-executed'
        | 'not-completed'
        | 'executed'
        | 'not-assigned'
        | 'in-a-locked-route',
    ) => {
      switch (orderStepStatus) {
        case 'not-executed': {
          switch (pairedOrderStepStatus) {
            case 'not-executed':
            case 'not-completed': {
              // This Drop-Off order is linked to a Pick-Up order. Do you want to move both orders?
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.title.pickupLinkedToDropoff',
                  })

                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.title.dropoffLinkedToPickup',
                  })
              }
            }
            case 'executed': {
              // This dropoff order is linked to a pickup order that has already been executed.
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.title.movePickupPairedExecutedDropoff',
                  })

                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.title.moveDropoffPairedExecutedPickup',
                  })
              }
            }
            case 'not-assigned': {
              // This dropoff order is linked to a pickup order that is not assigned to a route. The dropoff will be moved to the new route, do you also want to assign the pickup?
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.title.movePickupPairedUnassignedDropoff',
                  })

                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.title.moveDropoffPairedUnassignedPickup',
                  })
              }
            }
            case 'in-a-locked-route': {
              // This dropoff order is linked to a pickup order in a locked route.
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.title.movePickupPairedLockedDropoff',
                  })

                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.title.moveDropoffPairedLockedPickup',
                  })
              }
            }
          }
        }
        case 'not-completed': {
          switch (pairedOrderStepStatus) {
            case 'not-executed':
            case 'not-completed': {
              // This Drop-Off order is linked to a Pick-Up order. Do you want to move both orders?
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.title.pickupLinkedToDropoff',
                  })

                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.title.dropoffLinkedToPickup',
                  })
              }
            }
            case 'executed': {
              // This dropoff order is linked to a pickup order that has already been executed.
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.title.movePickupPairedExecutedDropoff',
                  })
                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.title.moveDropoffPairedExecutedPickup',
                  })
              }
            }
            case 'not-assigned': {
              // This dropoff order is linked to a pickup order that is not assigned to a route. The dropoff will be moved to the new route, do you also want to assign the pickup?
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.title.movePickupPairedUnassignedDropoff',
                  })
                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.title.moveDropoffPairedUnassignedPickup',
                  })
              }
            }
            case 'in-a-locked-route': {
              // This dropoff order is linked to a pickup order in a locked route.
              return translate({
                id: 'modals.moveOrders.header.title.pairedCantBeMoved',
              })
            }
          }
        }
        case 'executed': {
          switch (pairedOrderStepStatus) {
            case 'not-executed':
            case 'not-completed': {
              // This dropoff order has been executed and is linked to a pickup order. You can move both orders to keep them paired, or move only this order.
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.title.movePickupExecutedAndPairedDropoff',
                  })

                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.title.moveDropoffExecutedAndPairedPickup',
                  })
              }
            }
            case 'executed': {
              // This dropoff order is linked to a pickup order that has already been executed.
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.title.bothPickupAndDropoffExecuted',
                  })

                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.title.bothDropoffAndPickupExecuted',
                  })
              }
            }
            case 'not-assigned': {
              // This executed dropoff order is linked to a pickup that is not assigned. The dropoff will be moved to the new route, do you also want to assign the pickup?
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.title.moveExecutedPickupPairedUnassignedDropoff',
                  })

                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.title.moveExecutedDropoffPairedUnassignedPickup',
                  })
              }
            }
            case 'in-a-locked-route': {
              // This executed order order is linked to a pickup order in a locked route. Since the paired order is locked, only the executed order will be moved.
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.title.moveExecutedPickupPairedLockedDropoff',
                  })

                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.title.moveExecutedDropoffPairedLockedPickup',
                  })
              }
            }
          }
        }
      }
    },

    cardTextPaired: (
      orderStepStatus: 'not-executed' | 'not-completed' | 'executed',
      orderStepType: uui.domain.client.rm.OrderStepIdentifier,
      pairedOrderStepStatus:
        | 'not-executed'
        | 'not-completed'
        | 'executed'
        | 'not-assigned'
        | 'in-a-locked-route',
    ) => {
      switch (orderStepStatus) {
        case 'not-executed': {
          switch (pairedOrderStepStatus) {
            case 'not-executed':
            case 'not-completed': {
              // Separating paired pickup and dropoff will create violations
              return translate({ id: 'modals.moveOrders.card.text' })
            }
            case 'executed': {
              // Moving the executed order will delete its execution data permanently.
              return translate({
                id: 'modals.moveOrders.card.text.executedPaired',
              })
            }
            case 'not-assigned':
            case 'in-a-locked-route':
              return ''
          }
        }
        case 'not-completed': {
          switch (pairedOrderStepStatus) {
            case 'not-executed':
            case 'not-completed': {
              // Separating paired pickup and dropoff will create violations
              return translate({ id: 'modals.moveOrders.card.text' })
            }
            case 'executed': {
              // Moving the executed order will delete its execution data permanently.
              return translate({
                id: 'modals.moveOrders.card.text.executedPaired',
              })
            }
            case 'not-assigned':
            case 'in-a-locked-route':
              return ''
          }
        }
        case 'executed': {
          switch (pairedOrderStepStatus) {
            case 'not-executed':
            case 'not-completed': {
              // Moving the dropoff order will delete its execution data and may cause pairing violations if only one is moved.
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.text.executedStep.pickup',
                  })

                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.text.executedStep.dropoff',
                  })
              }
            }
            case 'executed': {
              // Moving them will delete their execution data and may cause pairing violations if only one is moved.
              return translate({
                id: 'modals.moveOrders.card.text.executedStep.both',
              })
            }
            case 'not-assigned': {
              // This executed dropoff order is linked to a pickup that is not assigned. The dropoff will be moved to the new route, do you also want to assign the pickup?
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.text.unassignedPaired.pickup',
                  })

                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.text.unassignedPaired.dropoff',
                  })
              }
            }
            case 'in-a-locked-route': {
              // This executed order order is linked to a pickup order in a locked route. Since the paired order is locked, only the executed order will be moved.
              switch (orderStepType) {
                case 'p':
                  return translate({
                    id: 'modals.moveOrders.card.text.lockedPaired.pickup',
                  })

                case 'd':
                  return translate({
                    id: 'modals.moveOrders.card.text.lockedPaired.dropoff',
                  })
              }
            }
          }
        }
      }
    },

    lockedRouteSuggestion: 'modals.moveOrders.lockedRouteSuggestion',

    btnErrorClose: translate({ id: 'modals.moveOrders.button.errorClose' }),

    errorCardTitle: translate({ id: 'modals.moveOrders.errorCard.title' }),

    errorCardText: (errorType: ErrorType) => {
      if (errorType === 'orderStepMissing' || errorType === 'routeUnavailable') {
        return translate({ id: `modals.moveOrders.errorCard.text.${errorType}` as const })
      } else {
        // don't know why all the status are not mapped in the lang files
        return ''
      }
    },
  }))

  return api
}
